// Port of ios/Sources/ViewModels/MessageAttachments.swift.
//
// Mattermost MESSAGE ATTACHMENTS — the coloured cards an integration posts, carried in the
// post's `props.attachments` and NOT in its `message` (shared/API.md; the webapp calls them
// attachments, which is unrelated to uploaded files).
//
// This client rendered none of it: a Jenkins channel was a column of avatars and timestamps
// with no content, because every one of those posts keeps its words here. The message IS the
// props. Android closed this on 2026-08-28; this is its `MessageAttachments`, rule for rule.
//
// Pure and unit-testable — no UI types, no store.

/** One field of the card's key/value grid. */
export interface AttachmentField {
  title: string;
  value: string;
  short: boolean;
}

/** One entry of a select menu: what the reader sees, and what gets submitted. */
export interface AttachmentOption {
  text: string;
  value: string;
}

/**
 * A control on an interactive message: a button, or a SELECT MENU.
 *
 * Dropping selects was a silent hole with a real cost: an attachment whose only content is
 * the dropped action measures as `isEmpty` and draws NO CARD AT ALL, so a plugin answering
 * with a menu would arrive as its one line of text and nothing else.
 *
 * ⚠️ This was added believing it was the Registro plugin's defect. It was NOT: that plugin
 * sends `type: button` actions (read from its source 2026-09-12 — shared/PLUGINS.md §9), and
 * what actually broke it was the ephemeral post's `system_ephemeral` TYPE plus the missing
 * action cookie. Selects remain supported because the shape is real and cheap to honour, but
 * no installed plugin is known to send one today, so treat it as unexercised.
 *
 * A select submits the same way a button does, plus the chosen option's VALUE — never its
 * label, which is what the reader saw and not what the integration asked for.
 */
export interface AttachmentAction {
  id: string;
  name: string;
  /** `primary` / `danger` / `default` — the sender's emphasis, honoured where known. */
  style: string;
  /** `button` or `select`. Anything else is dropped, as before. */
  type: string;
  /** A select's choices, in the order the integration listed them. Empty for a button. */
  options: AttachmentOption[];
  /**
   * The server's own signed blob for this control, and the thing that makes an EPHEMERAL
   * card answerable at all.
   *
   * `AddPostActionCookies` stamps it on every action before the post is broadcast, and
   * `DoPostActionWithCookie` needs it whenever the post cannot be loaded from the database
   * — which is always for an ephemeral post, because it was never stored. Without it the
   * press is a 404 (`app.post.get.app_error`) and the plugin never hears about it. The
   * server's own comment says as much: "the clients should hold on to it, and include it
   * with subsequent DoPostAction requests".
   *
   * Held opaque. It encrypts the integration URL and context, and a client that tried to
   * read or rebuild any of it would be inventing authority it does not have.
   */
  cookie: string | null;
}

/**
 * One press, packed: WHICH control, the chosen value when it was a menu, and the server's
 * cookie. Three loose arguments through four layers of UI is how the cookie went missing in
 * the first place — a card's answer is one thing, so it travels as one thing.
 */
export interface AttachmentAnswer {
  actionId: string;
  selectedOption: string | null;
  cookie: string | null;
}

export interface Attachment {
  /** `#RRGGBB` accent, drawn as the left rule. Null when the sender did not pick one. */
  color: string | null;
  pretext: string;
  authorName: string;
  title: string;
  titleLink: string;
  text: string;
  fields: AttachmentField[];
  actions: AttachmentAction[];
  footer: string;
  imageUrl: string;
  thumbUrl: string;
}

/**
 * Everything a message row reads out of `props`, from ONE decode.
 *
 * Each accessor here used to deserialise the whole props blob for its own question, and a row
 * asks four of them — inside `body`, so EVERY render of a bot post paid for four JSON parses.
 * In an ordinary conversation that is invisible; in a channel that is nothing but integration
 * posts with cards and buttons on them (the recruitment channels, 2026-09-16) it is the
 * per-row cost of scrolling.
 *
 * Kotlin twin: `MessageAttachments.PostProps`.
 */
export interface PostProps {
  attachments: Attachment[];
  webhookUsername: string;
  webhookIconUrl: string;
  isFromWebhook: boolean;
}

const typeButton = 'button';
const typeSelect = 'select';

const NONE: PostProps = Object.freeze({
  attachments: [],
  webhookUsername: '',
  webhookIconUrl: '',
  isFromWebhook: false,
}) as PostProps;

function action(
  id: string,
  name: string,
  style: string,
  type: string = typeButton,
  options: AttachmentOption[] = [],
  cookie: string | null = null,
): AttachmentAction {
  return { id, name, style, type, options, cookie };
}

function answer(actionId: string, selectedOption: string | null = null, cookie: string | null = null): AttachmentAnswer {
  return { actionId, selectedOption, cookie };
}

function isSelect(a: AttachmentAction): boolean {
  return a.type === typeSelect;
}

/** A card with no words AND no buttons in it is not worth a box. */
function isEmpty(a: Attachment): boolean {
  return (
    a.pretext === '' &&
    a.authorName === '' &&
    a.title === '' &&
    a.text === '' &&
    a.fields.length === 0 &&
    a.actions.length === 0 &&
    a.footer === '' &&
    a.imageUrl === '' &&
    a.thumbUrl === ''
  );
}

// ---- the decode cache ----
//
// The decoded props of the rows a reader has recently looked at. Bounded (the Apple twin is an
// NSCache with countLimit 256): several screens of conversation. Keyed by the props string itself,
// so two posts sharing a card (a repeated bot template) share the entry. Oldest-inserted entry is
// evicted first; losing one costs one parse.
const CACHE_LIMIT = 256;
const propsCache = new Map<string, PostProps>();

function cacheStore(key: string, value: PostProps): void {
  if (propsCache.size >= CACHE_LIMIT && !propsCache.has(key)) {
    const oldest = propsCache.keys().next().value;
    if (oldest !== undefined) propsCache.delete(oldest);
  }
  propsCache.set(key, value);
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Integrations put numbers and bools where strings belong; the webapp coerces, so we do.
 *
 * Mirrors Swift's `NSNumber.stringValue` on a JSONSerialization value: a JSON number prints as
 * its number, and a JSON BOOL is an NSNumber too, which prints as "1"/"0" — so `from_webhook: true`
 * (a bool, not Mattermost's string "true") is NOT read as a webhook on the Apple client either.
 */
function stringValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? '1' : '0';
  return '';
}

/** Swift's `as Bool` on a JSON value: a real bool, or the NSNumber 1/0 that bridges to one. */
function boolValue(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
}

function trimmed(value: unknown): string {
  return stringValue(value).trim();
}

function parseFields(array: unknown): AttachmentField[] {
  if (!Array.isArray(array)) return [];
  const out: AttachmentField[] = [];
  for (const entry of array) {
    if (!isObject(entry)) continue;
    const field: AttachmentField = {
      title: trimmed(entry.title),
      value: trimmed(entry.value),
      short: boolValue(entry.short),
    };
    if (field.title === '' && field.value === '') continue;
    out.push(field);
  }
  return out;
}

/**
 * `text` is the label and `value` is what the integration receives. An option missing either
 * is unusable: with no value there is nothing to submit, and with no text there is nothing to
 * choose. Mattermost also sends numeric values, which `stringValue` renders correctly.
 */
function parseOptions(array: unknown): AttachmentOption[] {
  if (!Array.isArray(array)) return [];
  const out: AttachmentOption[] = [];
  for (const entry of array) {
    if (!isObject(entry)) continue;
    const text = trimmed(entry.text);
    const value = trimmed(entry.value);
    if (text === '' || value === '') continue;
    out.push({ text, value });
  }
  return out;
}

function parseActions(array: unknown): AttachmentAction[] {
  if (!Array.isArray(array)) return [];
  const out: AttachmentAction[] = [];
  for (const entry of array) {
    if (!isObject(entry)) continue;
    // Mattermost defaults a missing type to `button`, and so does this.
    const rawType = trimmed(entry.type);
    const type = rawType === '' ? typeButton : rawType;
    if (type !== typeButton && type !== typeSelect) continue;
    const id = trimmed(entry.id);
    const name = trimmed(entry.name);
    // Both are needed: an id is how the answer is reported, a name is what the reader
    // presses. Either missing makes the control unusable rather than merely plain.
    if (id === '' || name === '') continue;
    const menuOptions = parseOptions(entry.options);
    // A menu with nothing in it cannot be answered, so it is not a control — dropping it
    // is the same judgement as dropping a nameless button.
    if (type === typeSelect && menuOptions.length === 0) continue;
    const cookie = trimmed(entry.cookie);
    out.push(action(id, name, trimmed(entry.style), type, menuOptions, cookie === '' ? null : cookie));
  }
  return out;
}

function parseAttachment(json: Record<string, unknown>): Attachment {
  const color = trimmed(json.color);
  return {
    color: color === '' ? null : color,
    pretext: trimmed(json.pretext),
    authorName: trimmed(json.author_name),
    title: trimmed(json.title),
    titleLink: trimmed(json.title_link),
    text: trimmed(json.text),
    fields: parseFields(json.fields),
    actions: parseActions(json.actions),
    footer: trimmed(json.footer),
    imageUrl: trimmed(json.image_url),
    thumbUrl: trimmed(json.thumb_url),
  };
}

/**
 * Decoded once per distinct props string and remembered, because the accessors are asked on
 * every render of a row, with the same string, for the life of the conversation.
 */
function decoded(propsJson: string | null | undefined): PostProps {
  if (!propsJson) return NONE;
  const cached = propsCache.get(propsJson);
  if (cached) return cached;
  let props: unknown;
  try {
    props = JSON.parse(propsJson);
  } catch {
    cacheStore(propsJson, NONE);
    return NONE;
  }
  if (!isObject(props)) {
    cacheStore(propsJson, NONE);
    return NONE;
  }
  const rawAttachments = Array.isArray(props.attachments) ? props.attachments : [];
  const result: PostProps = {
    attachments: rawAttachments
      .filter(isObject)
      .map(parseAttachment)
      .filter((a) => !isEmpty(a)),
    webhookUsername: trimmed(props.override_username),
    webhookIconUrl: trimmed(props.override_icon_url),
    isFromWebhook: stringValue(props.from_webhook).toLowerCase() === 'true',
  };
  cacheStore(propsJson, result);
  return result;
}

/**
 * The cards on a post, or empty for an ordinary message. Never throws: props are
 * server-shaped JSON this client does not control, and a malformed card must cost its own
 * content, never the message it rides on.
 */
function parse(propsJson: string | null | undefined): Attachment[] {
  return decoded(propsJson).attachments;
}

function postProps(propsJson: string | null | undefined): PostProps {
  return decoded(propsJson);
}

/**
 * The name an integration asked to post under (`override_username`), or empty. Without it a
 * webhook post shows the BOT account behind it — or the unknown-author "?" when that account
 * was never fetched.
 */
function webhookUsername(propsJson: string | null | undefined): string {
  return decoded(propsJson).webhookUsername;
}

/**
 * The picture an integration asked to post under (`override_icon_url`), or empty.
 *
 * Mattermost draws it; we did not, so an integration's post wore the coloured initials of
 * whatever account the webhook belongs to (reported 2026-09-10 with both clients side by
 * side). The name has been overridden for a while; the face was the half that never was.
 */
function webhookIconUrl(propsJson: string | null | undefined): string {
  return decoded(propsJson).webhookIconUrl;
}

/**
 * Whether the post came from an integration rather than a person — `from_webhook`, which
 * Mattermost sets as the STRING "true".
 *
 * The BOT tag hangs off this and not off the author's `isBot`: an incoming webhook posts as
 * the human who owns it, so `isBot` is false on precisely the posts that most need the tag.
 */
function isFromWebhook(propsJson: string | null | undefined): boolean {
  return decoded(propsJson).isFromWebhook;
}

/**
 * Whether this post is an acknowledgement form (`com.satoritech.acknowledgements`).
 *
 * The post carries no flag of its own: what identifies it is that its interactive actions
 * point at the plugin. Reading the raw props for the plugin id is exact and costs nothing —
 * the alternative, asking the server about every post on screen, would be a request per row.
 */
function isAcknowledgement(propsJson: string | null | undefined): boolean {
  return propsJson != null && propsJson.includes('com.satoritech.acknowledgements');
}

export const MessageAttachments = {
  typeButton,
  typeSelect,
  none: NONE,
  action,
  answer,
  isSelect,
  isEmpty,
  parse,
  postProps,
  webhookUsername,
  webhookIconUrl,
  isFromWebhook,
  isAcknowledgement,
} as const;
